import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowRight, Plane, Loader2, Shield, ExternalLink, Sparkles, CheckCircle2 } from "lucide-react";

import { widget } from "@/lib/api";
import Brandmark from "@/components/Brandmark";

/* Embeddable Quote Widget — public, no auth.
   - 3 screens: form → loading → results
   - branded "Private Jet One" ONLY
   - operator identity never appears (server enforces THE WALL)
   Also exports PublicQuote — the self-serve /q/:token quote page (the link in
   every quote-ready / follow-up email) with the accept-and-book CTA. */

const HERO_URL =
  "https://images.unsplash.com/photo-1638555063519-d009e6f3b28b?auto=format&fit=crop&w=2000&q=80";

// Acquisition attribution forwarded verbatim from the landing URL into the
// quote payload (WidgetQuoteIn accepts them; GROWTH attributes booked revenue).
const ATTRIBUTION_KEYS = ["utm_source", "utm_medium", "utm_campaign", "partner", "ref"];

// Cabin classes PRISM understands (TripRequest.preferred_cabin literals).
const CABINS = [
  { value: "light", label: "Light Jet" },
  { value: "midsize", label: "Midsize Jet" },
  { value: "super_mid", label: "Super-Midsize Jet" },
  { value: "heavy", label: "Heavy Jet" },
  { value: "ultra_long", label: "Ultra-Long-Range Jet" },
];

/* Parse the landing URL once on mount: route/date/pax prefill (ATLAS CTAs link
   /widget?from=KTEB&to=KMIA&utm_source=atlas&utm_campaign={slug}) + attribution. */
function readUrlParams() {
  if (typeof window === "undefined") return {};
  const sp = new URLSearchParams(window.location.search);
  const out = {};
  for (const k of ["from", "to", "date", "pax", ...ATTRIBUTION_KEYS]) {
    const v = sp.get(k);
    if (v) out[k] = v;
  }
  return out;
}

function attributionFrom(params) {
  const out = {};
  for (const k of ATTRIBUTION_KEYS) {
    if (params[k]) out[k] = params[k];
  }
  return out;
}

// ?date= → datetime-local input value (local clock), or null when unparseable.
function toLocalInputValue(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

export default function Widget() {
  const [step, setStep] = useState("form");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [contact, setContact] = useState({ full_name: "", email: "" });
  const urlParams = useMemo(readUrlParams, []);

  const onSubmit = async (form) => {
    setStep("loading");
    setError(null);
    setContact({ full_name: form.full_name || "", email: form.email || "" });
    try {
      const r = await widget.quote(form);
      if (r.ok) {
        setResult(r);
        setStep("results");
      } else {
        setError(r.reason || "No supply available right now.");
        setStep("form");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Quote failed");
      setStep("form");
    }
  };

  return (
    <div
      data-testid="page-widget"
      className="noise-overlay min-h-screen flex flex-col"
      style={{
        background: `linear-gradient(180deg, rgba(244,239,230,0.78) 0%, rgba(244,239,230,0.90) 60%, rgba(244,239,230,0.97) 100%), url(${HERO_URL}) center/cover no-repeat fixed`,
      }}
    >
      <header className="px-6 py-5 md:px-12 md:py-6 flex items-center justify-between gap-4">
        <Brandmark />
        <a
          href="/"
          data-testid="widget-ops-link"
          className="mono text-[10px] uppercase tracking-[0.16em] text-[rgb(var(--dim))] hover:text-[rgb(var(--fg))] inline-flex items-center gap-1.5 shrink-0 whitespace-nowrap"
        >
          Ops Console <ExternalLink size={11} />
        </a>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-6 md:py-10">
        <div className="w-full max-w-md">
          {step === "form" && <QuoteForm onSubmit={onSubmit} error={error} prefill={urlParams} />}
          {step === "loading" && <LoadingState />}
          {step === "results" && result && (
            <ResultsView result={result} contact={contact} onReset={() => setStep("form")} />
          )}
        </div>
      </main>

      <footer className="px-6 py-5 md:px-12 md:py-6 mono text-[10px] uppercase tracking-[0.16em] text-[rgb(var(--dim))] flex items-center justify-between flex-wrap gap-2">
        <span className="inline-flex items-center gap-2">
          <Shield size={11} /> 14 CFR Part 295 broker · operator identity disclosed before flight on request
        </span>
        <span>© Private Jet One</span>
      </footer>
    </div>
  );
}

function QuoteForm({ onSubmit, error, prefill = {} }) {
  const tomorrow = new Date(Date.now() + 86400000 * 3).toISOString().slice(0, 16);
  const [from_airport, setFrom] = useState((prefill.from || "KJFK").toUpperCase());
  const [to_airport, setTo] = useState((prefill.to || "KLAS").toUpperCase());
  const [departure_at, setDep] = useState(toLocalInputValue(prefill.date) || tomorrow);
  const [pax, setPax] = useState(Number(prefill.pax) >= 1 ? Number(prefill.pax) : 4);
  const [cabin, setCabin] = useState("midsize");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  // CAN-SPAM/GDPR: marketing consent is an explicit, default-OFF opt-in — the
  // audience gate server-side only captures the list entry when this is true.
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [gateError, setGateError] = useState(null);

  const submit = (e) => {
    e.preventDefault();
    // Soft lead-gate: an email OR phone is required before sourcing runs, so
    // every set of options shown is attached to a reachable lead.
    if (!email.trim() && !phone.trim()) {
      setGateError("Add an email or phone number so we can hold your options for you.");
      return;
    }
    setGateError(null);
    onSubmit({
      full_name: name || null,
      email: email.trim() || null,
      phone: phone.trim() || null,
      pax: Number(pax),
      preferred_cabin: cabin,
      legs: [{ from_airport, to_airport, departure_at: new Date(departure_at).toISOString() }],
      consent_marketing: consentMarketing,
      consent_enrichment: false,
      ...attributionFrom(prefill),
    });
  };

  return (
    <form
      data-testid="widget-form"
      onSubmit={submit}
      className="pj-fade-in"
      style={{
        background: "rgba(252,249,243,0.82)",
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        border: "1px solid rgba(190,176,152,0.45)",
        padding: 28,
      }}
    >
      <div className="pj-label mb-3 flex items-center gap-2">
        <Sparkles size={11} /> · Instant Quote · under 60 seconds
      </div>
      <h1 className="font-display text-[34px] leading-[1.05] mb-1" data-testid="widget-title">
        Where would you like to go?
      </h1>
      <p className="mono text-[11px] text-[rgb(var(--dim))] mb-6">
        Three branded options ready in a minute. No operator chasing, no calls.
      </p>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <AirportField label="From (city or airport)" value={from_airport} onChange={setFrom} testid="widget-from" />
        <AirportField label="To (city or airport)" value={to_airport} onChange={setTo} testid="widget-to" />
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <WField label="Departure" value={departure_at} onChange={setDep} type="datetime-local" testid="widget-dep" />
        <WField label="Passengers" value={pax} onChange={setPax} type="number" testid="widget-pax" />
      </div>
      <label className="block mb-3">
        <span className="pj-label block mb-1.5">Cabin</span>
        <select
          value={cabin}
          onChange={(e) => setCabin(e.target.value)}
          data-testid="widget-cabin"
          className="w-full mono text-[12px] px-3 py-2.5"
          style={{
            background: "rgba(244,239,230,0.75)",
            border: "1px solid rgba(190,176,152,0.45)",
            color: "rgb(38,31,24)",
          }}
        >
          {CABINS.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </label>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <WField label="Email" value={email} onChange={setEmail} type="email" testid="widget-email" />
        <WField label="Phone" value={phone} onChange={setPhone} type="tel" testid="widget-phone" />
      </div>
      <WField label="Name (optional)" value={name} onChange={setName} testid="widget-name" />

      <label className="mt-4 flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={consentMarketing}
          onChange={(e) => setConsentMarketing(e.target.checked)}
          data-testid="widget-consent"
          className="mt-0.5"
        />
        <span className="mono text-[10px] text-[rgb(var(--dim))] leading-[1.5]">
          Send me empty-leg deals and route price drops (optional — unsubscribe any time).
        </span>
      </label>

      {gateError && (
        <div className="mt-4 mono text-[11px]" style={{ color: "rgb(196,52,44)" }} data-testid="widget-leadgate-error">
          {gateError}
        </div>
      )}
      {error && (
        <div className="mt-4 mono text-[11px]" style={{ color: "rgb(196,52,44)" }}>
          {error}
        </div>
      )}

      <button
        type="submit"
        data-testid="widget-submit"
        className="mt-5 w-full inline-flex items-center justify-between px-4 py-3 mono uppercase text-[11px] tracking-[0.14em] hover:opacity-90"
        style={{ background: "linear-gradient(90deg, rgb(169,130,28), rgb(158,92,44))", color: "rgb(252,249,243)" }}
      >
        Get 3 Instant Quotes <ArrowRight size={14} />
      </button>
    </form>
  );
}

function WField({ label, value, onChange, type = "text", testid, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="pj-label block mb-1.5">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full mono text-[12px] px-3 py-2.5"
        style={{
          background: "rgba(244,239,230,0.75)",
          border: "1px solid rgba(190,176,152,0.45)",
          color: "rgb(38,31,24)",
        }}
      />
    </label>
  );
}

/* From/To typeahead — searches city/name/IATA/ICAO via GET /api/public/airports
   (rate-limited server-side); picking a suggestion fills the ICAO code the quote
   pipeline expects. Free typing still works (codes pass straight through). */
function AirportField({ label, value, onChange, testid }) {
  const [options, setOptions] = useState([]);
  const seqRef = useRef(0);

  const handleChange = (v) => {
    onChange(v);
    const q = v.trim();
    const seq = ++seqRef.current;
    if (q.length < 2) {
      setOptions([]);
      return;
    }
    widget
      .airports(q)
      .then((r) => {
        if (seq === seqRef.current) setOptions(r.airports || []);
      })
      .catch(() => {}); // typeahead is best-effort — never block typing
  };

  const pick = (a) => {
    seqRef.current += 1; // drop any in-flight lookup
    onChange(a.code);
    setOptions([]);
  };

  return (
    <div className="relative">
      <WField label={label} value={value} onChange={handleChange} testid={testid} />
      {options.length > 0 && (
        <ul
          data-testid={`${testid}-suggestions`}
          className="absolute z-10 left-0 right-0 mt-1 max-h-48 overflow-auto"
          style={{
            background: "rgba(252,249,243,0.97)",
            border: "1px solid rgba(190,176,152,0.45)",
          }}
        >
          {options.map((a) => (
            <li key={a.code}>
              <button
                type="button"
                data-testid={`${testid}-opt-${a.code}`}
                onClick={() => pick(a)}
                className="w-full text-left px-3 py-2 mono text-[11px] hover:opacity-80"
              >
                {a.city || a.name} · {a.name} ({a.iata || a.code})
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LoadingState() {
  const phases = [
    "Sourcing aircraft across the network…",
    "Pricing all-in totals…",
    "Vetting operators (back-stage)…",
    "Attaching 14 CFR Part 295 disclosure…",
  ];
  return (
    <div
      className="pj-fade-in"
      style={{
        background: "rgba(252,249,243,0.82)",
        backdropFilter: "blur(24px)",
        border: "1px solid rgba(190,176,152,0.45)",
        padding: 28,
      }}
      data-testid="widget-loading"
    >
      <div className="flex items-center gap-2">
        <Loader2 className="animate-spin" size={14} />
        <span className="mono text-[10px] uppercase tracking-[0.16em] text-[rgb(var(--dim))]">Live sourcing</span>
      </div>
      <ul className="mt-4 space-y-2">
        {phases.map((p, i) => (
          <li key={p} className="mono text-[12px] flex items-center gap-2 pj-fade-in" style={{ animationDelay: `${i * 220}ms` }}>
            <span className="pj-pulse" style={{ width: 6, height: 6, background: "rgb(169,130,28)", display: "inline-block" }} />
            <span className="text-[rgb(var(--fg))]">{p}</span>
          </li>
        ))}
      </ul>
      <div className="mt-6 relative h-1 overflow-hidden pj-marquee" style={{ background: "rgba(190,176,152,0.30)" }} />
    </div>
  );
}

const CARD_STYLE = {
  background: "rgba(252,249,243,0.82)",
  backdropFilter: "blur(24px)",
  border: "1px solid rgba(190,176,152,0.45)",
};

function OptionCard({ option: o, testidPrefix, children }) {
  return (
    <div
      data-testid={`${testidPrefix}-option-${o.tier}`}
      className="pj-fade-in"
      style={{ ...CARD_STYLE, padding: 18 }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="mono text-[10px] uppercase tracking-[0.18em] text-[rgb(var(--dim))] truncate">{o.tier}</div>
          <div className="font-display text-[18px] mt-1 truncate" title={o.model_name}>{o.model_name}</div>
          <div className="mono text-[10px] text-[rgb(var(--dim))] capitalize mt-0.5 break-words">
            {o.cabin.replace("_"," ")} · {o.seats} seats · {o.eta_minutes ? `~${Math.round(o.eta_minutes / 60)}h flight` : ""}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="mono text-[18px] text-[rgb(var(--fg))] whitespace-nowrap">
            ${o.total_usd.toLocaleString("en-US", { maximumFractionDigits: 0 })}
          </div>
          <div className="mono text-[9px] uppercase tracking-[0.18em] text-[rgb(var(--dim))] whitespace-nowrap">all-in · USD</div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 mono text-[10px] text-[rgb(var(--dim))]">
        {Object.entries(o.breakdown).slice(0, 4).map(([k, v]) => (
          <div key={k} className="min-w-0">
            <div className="pj-label truncate" style={{ fontSize: 8 }} title={k.replace("_", " ")}>{k.replace("_", " ")}</div>
            <div className="mt-1 text-[rgb(var(--fg))] truncate">${Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}</div>
          </div>
        ))}
      </div>
      {children}
    </div>
  );
}

/* Per-option self-serve accept-and-book CTA. Identity is resolved server-side
   from the lead; the inline contact fields cover anonymous quotes (the server
   answers contact_required when it has no email on file). */
function AcceptCTA({ token, option, defaults = {}, testidPrefix, label = "Reserve", onAccepted }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(defaults.full_name || "");
  const [email, setEmail] = useState(defaults.email || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await widget.accept(token, {
        option_id: option.id,
        full_name: name || null,
        email: email || null,
      });
      onAccepted(r);
    } catch (e) {
      const code = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (code === 409) setError("This quote has already been booked.");
      else if (code === 410) setError("This quote has expired — please request a fresh one.");
      else if (detail === "contact_required") setError("Add your email so we can confirm your booking.");
      else setError(detail || "Reservation failed — please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`${testidPrefix}-reserve-${option.tier}`}
        className="mt-4 w-full inline-flex items-center justify-between px-4 py-2.5 mono uppercase text-[11px] tracking-[0.14em] hover:opacity-90"
        style={{ background: "linear-gradient(90deg, rgb(169,130,28), rgb(158,92,44))", color: "rgb(252,249,243)" }}
      >
        {label} <ArrowRight size={13} />
      </button>
    );
  }
  return (
    <div className="mt-4 space-y-3" data-testid={`${testidPrefix}-accept-form-${option.tier}`}>
      <WField label="Name" value={name} onChange={setName} testid={`${testidPrefix}-accept-name-${option.tier}`} />
      <WField label="Email" value={email} onChange={setEmail} type="email" testid={`${testidPrefix}-accept-email-${option.tier}`} />
      {error && (
        <div className="mono text-[11px]" style={{ color: "rgb(196,52,44)" }} data-testid={`${testidPrefix}-accept-error-${option.tier}`}>
          {error}
        </div>
      )}
      <button
        type="button"
        onClick={confirm}
        disabled={busy}
        data-testid={`${testidPrefix}-confirm-${option.tier}`}
        className="w-full inline-flex items-center justify-between px-4 py-2.5 mono uppercase text-[11px] tracking-[0.14em] hover:opacity-90 disabled:opacity-40"
        style={{ background: "linear-gradient(90deg, rgb(169,130,28), rgb(158,92,44))", color: "rgb(252,249,243)" }}
      >
        {busy ? "Booking…" : "Confirm reservation"} <CheckCircle2 size={13} />
      </button>
    </div>
  );
}

function BookedPanel({ ack, testidPrefix }) {
  return (
    <div className="pj-fade-in" style={{ ...CARD_STYLE, padding: 20 }} data-testid={`${testidPrefix}-booked`}>
      <div className="pj-label mb-2 flex items-center gap-2">
        <CheckCircle2 size={11} /> · Reservation confirmed
      </div>
      <h2 className="font-display text-[22px] leading-tight">
        {ack.blocked ? "Almost there — final checks in progress." : "You're booked."}
      </h2>
      <p className="mono text-[11px] text-[rgb(var(--dim))] mt-2">
        {ack.blocked
          ? "Our team is completing a quick verification. You'll receive your contract by email shortly."
          : "Your charter agreement and itinerary are on the way to your inbox."}
      </p>
      <p className="mono text-[11px] mt-2">
        Reference: <strong>{ack.reference || ack.booking_id}</strong>
      </p>
    </div>
  );
}

function ResultsView({ result, contact = {}, onReset }) {
  const q = result.quote;
  const [booked, setBooked] = useState(null);
  return (
    <div className="pj-fade-in space-y-3" data-testid="widget-results">
      <div style={{ ...CARD_STYLE, padding: 20 }}>
        <div className="flex items-center justify-between gap-3">
          <div className="pj-label truncate">Quote · {q.share_token.slice(0, 8).toUpperCase()}</div>
          <span className="mono text-[10px] uppercase tracking-[0.16em] text-[rgb(var(--dim))] shrink-0 whitespace-nowrap">
            {result.latency_ms}ms
          </span>
        </div>
        <h2 className="font-display text-[22px] mt-2 leading-tight">
          Three options ready for you.
        </h2>
        {q.llm_summary && (
          <p className="mono text-[12px] text-[rgb(var(--fg))] mt-2">{q.llm_summary}</p>
        )}
        {q.valid_until && (
          <p
            className="mono text-[11px] mt-2"
            style={{ color: "rgb(169,130,28)" }}
            data-testid="widget-valid-until"
          >
            Price locked until {new Date(q.valid_until).toLocaleString()}
          </p>
        )}
      </div>

      {booked && <BookedPanel ack={booked} testidPrefix="widget" />}

      {!booked &&
        q.options.map((o) => (
          <OptionCard key={o.id} option={o} testidPrefix="widget">
            <AcceptCTA
              token={q.share_token}
              option={o}
              defaults={contact}
              testidPrefix="widget"
              onAccepted={setBooked}
            />
          </OptionCard>
        ))}

      <p className="mono text-[10px] text-[rgb(var(--dim))] leading-[1.55] mt-2">
        {q.part295_disclosure}
      </p>

      <button
        type="button"
        onClick={onReset}
        data-testid="widget-reset"
        className="w-full mono inline-flex items-center justify-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.14em] border border-[rgb(var(--border-strong))] text-[rgb(var(--fg))] hover:border-[rgb(169,130,28)] hover:text-[rgb(169,130,28)]"
      >
        <Plane size={12} /> Quote another trip
      </button>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   PublicQuote — the self-serve /q/:token page. Renders the client-safe quote
   fetched by share token (the capability link in every quote email) with a
   per-option accept-and-book CTA. No auth; THE WALL is enforced server-side. */
export function PublicQuote() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | notfound
  const [booked, setBooked] = useState(null);

  useEffect(() => {
    let live = true;
    setState("loading");
    widget
      .byShareToken(token)
      .then((q) => {
        if (!live) return;
        setQuote(q);
        setState("ready");
      })
      .catch(() => live && setState("notfound"));
    return () => {
      live = false;
    };
  }, [token]);

  const alreadyAccepted = quote && ["accepted", "booked"].includes(quote.status);
  const expired = quote && quote.status === "expired";

  return (
    <div
      data-testid="page-public-quote"
      className="noise-overlay min-h-screen flex flex-col"
      style={{
        background: `linear-gradient(180deg, rgba(244,239,230,0.78) 0%, rgba(244,239,230,0.90) 60%, rgba(244,239,230,0.97) 100%), url(${HERO_URL}) center/cover no-repeat fixed`,
      }}
    >
      <header className="px-6 py-5 md:px-12 md:py-6 flex items-center justify-between gap-4">
        <Brandmark />
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-6 md:py-10">
        <div className="w-full max-w-md space-y-3">
          {state === "loading" && (
            <div className="pj-fade-in flex items-center gap-2 mono text-[11px] text-[rgb(var(--dim))]" data-testid="pq-loading">
              <Loader2 className="animate-spin" size={14} /> Loading your quote…
            </div>
          )}

          {state === "notfound" && (
            <div className="pj-fade-in" style={{ ...CARD_STYLE, padding: 20 }} data-testid="pq-notfound">
              <h2 className="font-display text-[22px] leading-tight">Quote not found.</h2>
              <p className="mono text-[11px] text-[rgb(var(--dim))] mt-2">
                This link may have expired. Request a fresh quote any time.
              </p>
            </div>
          )}

          {state === "ready" && quote && (
            <>
              <div style={{ ...CARD_STYLE, padding: 20 }}>
                <div className="pj-label truncate">
                  Quote · {(quote.share_token || token).slice(0, 8).toUpperCase()}
                </div>
                <h2 className="font-display text-[22px] mt-2 leading-tight" data-testid="pq-title">
                  Your private charter options.
                </h2>
                {quote.llm_summary && (
                  <p className="mono text-[12px] text-[rgb(var(--fg))] mt-2">{quote.llm_summary}</p>
                )}
              </div>

              {booked && <BookedPanel ack={booked} testidPrefix="pq" />}
              {!booked && alreadyAccepted && (
                <div style={{ ...CARD_STYLE, padding: 20 }} data-testid="pq-already-accepted">
                  <p className="mono text-[12px]">
                    This quote has already been booked. Check your inbox for your contract and itinerary.
                  </p>
                </div>
              )}
              {!booked && expired && (
                <div style={{ ...CARD_STYLE, padding: 20 }} data-testid="pq-expired">
                  <p className="mono text-[12px]">
                    This quote has expired — request a fresh one and we'll reprice it instantly.
                  </p>
                </div>
              )}

              {!booked &&
                (quote.options || []).map((o) => (
                  <OptionCard key={o.id} option={o} testidPrefix="pq">
                    {!alreadyAccepted && !expired && (
                      <AcceptCTA
                        token={token}
                        option={o}
                        testidPrefix="pq"
                        label="Accept & book"
                        onAccepted={setBooked}
                      />
                    )}
                  </OptionCard>
                ))}

              <p className="mono text-[10px] text-[rgb(var(--dim))] leading-[1.55] mt-2">
                {quote.part295_disclosure}
              </p>
            </>
          )}
        </div>
      </main>

      <footer className="px-6 py-5 md:px-12 md:py-6 mono text-[10px] uppercase tracking-[0.16em] text-[rgb(var(--dim))] flex items-center justify-between flex-wrap gap-2">
        <span className="inline-flex items-center gap-2">
          <Shield size={11} /> 14 CFR Part 295 broker · operator identity disclosed before flight on request
        </span>
        <span>© Private Jet One</span>
      </footer>
    </div>
  );
}
